import React, { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const MAX_KB = 400; // client-side compressed target
const MAX_SIDE = 480; // px, compress to this longest edge

async function compressToDataUrl(file) {
  if (!file.type.startsWith("image/")) throw new Error("Berkas harus berupa gambar");
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0, w, h);
  // Try PNG first (preserves transparency for signatures/stamps), fallback to JPEG if too big
  let url = canvas.toDataURL("image/png");
  if (url.length > MAX_KB * 1024 * 1.37) {
    url = canvas.toDataURL("image/jpeg", 0.82);
  }
  return url;
}

/**
 * Drag-and-drop image field with preview.
 * Props: label, value (data URL or http URL), onChange(dataUrl|"").
 */
export default function ImageDropField({ label, value, onChange, testId }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await compressToDataUrl(file);
      onChange(url);
    } catch (e) {
      toast.error(e.message || "Gagal memproses gambar");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <label className="block text-[0.7rem] font-bold tracking-wider text-[hsl(var(--muted-foreground))] uppercase mb-1">{label}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        className={`relative border-2 border-dashed rounded-lg p-3 transition ${dragging ? "border-emerald-500 bg-emerald-50" : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30"}`}
        data-testid={testId}
      >
        {value ? (
          <div className="flex items-center gap-3">
            <img src={value} alt="preview" className="w-16 h-16 object-contain bg-white border rounded" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-[#0e2b26]">Gambar tersimpan</div>
              <div className="text-[0.7rem] text-[hsl(var(--muted-foreground))] truncate">
                {value.startsWith("data:") ? `Uploaded (${Math.round(value.length / 1024)} KB)` : value}
              </div>
              <div className="flex gap-1.5 mt-1.5">
                <button type="button" onClick={() => inputRef.current?.click()} className="text-[0.7rem] font-semibold text-[#0e6b4f] hover:underline">Ganti</button>
                <button type="button" onClick={() => onChange("")} className="text-[0.7rem] font-semibold text-red-600 hover:underline inline-flex items-center gap-0.5"><X size={10} /> Hapus</button>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-full flex flex-col items-center justify-center gap-1.5 py-3 text-[hsl(var(--muted-foreground))] hover:text-[#0e2b26]"
          >
            {busy ? <div className="animate-pulse text-xs">Memproses...</div> : (
              <>
                <div className="w-9 h-9 rounded-full bg-white border border-[hsl(var(--border))] flex items-center justify-center">
                  <Upload size={16} />
                </div>
                <div className="text-xs font-semibold">Klik atau seret berkas ke sini</div>
                <div className="text-[0.68rem]">PNG / JPG · maks ~{MAX_KB} KB (otomatis dikompresi)</div>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
